import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowUpDown, AlertTriangle, MessageSquare, Check, X, Trash2, Send, ArrowRight, ExternalLink } from 'lucide-react';
import { salesControllingApi } from '../../services/salesControllingApi';
import { usePagePermissions } from '../../hooks/usePagePermissions';
import { useToast } from '../ui/Toast';
import { SearchableSelect } from '../ui/SearchableSelect';
import type { SCFilters, SCFilterOptions, PriceOverviewRow, SCComment, PriceOverviewSummary } from '../../types/salesControlling';

const fmt = (n: number, decimals = 2) =>
    n.toLocaleString('de-CH', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

function formatCommentDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
        ' ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

function formatEmail(email: string): string {
    const at = email.indexOf('@');
    return at > 0 ? email.substring(0, at) : email;
}

import { getEosInvoiceUrl } from '../../utils/bcLinks';

interface Props {
    filters: SCFilters;
    filterOptions: SCFilterOptions;
    refreshKey: number;
    onNavigateToSurcharge?: (documentCode: string) => void;
}

export function PriceOverviewTab({ filters, refreshKey, onNavigateToSurcharge }: Props) {
    const { toast } = useToast();
    const { canEdit } = usePagePermissions('/sales-controlling');
    const [rows, setRows] = useState<PriceOverviewRow[]>([]);
    const [summary, setSummary] = useState<PriceOverviewSummary>({ totalQty: 0, totalNetSales: 0, totalGrossProfit: 0, avgUnitPrice: 0, avgCalcPrice: 0, avgUnitCost: 0, avgMarginPct: 0, avgMargin12M: null });
    const [availableSalesManagers, setAvailableSalesManagers] = useState<Array<{ id: string; name: string }>>([]);
    const [availableSuppliers, setAvailableSuppliers] = useState<Array<{ id: string; name: string }>>([]);
    const [availableCustomers, setAvailableCustomers] = useState<Array<{ id: string; name: string }>>([]);
    const [availableProducts, setAvailableProducts] = useState<Array<{ id: string; name: string }>>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState('PostingDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [marginEnabled, setMarginEnabled] = useState(false);
    const [marginThreshold, setMarginThreshold] = useState(5);
    const [deviationEnabled, setDeviationEnabled] = useState(false);
    const [deviationThreshold, setDeviationThreshold] = useState(10);
    const [customerFilter, setCustomerFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [salesManagerFilter, setSalesManagerFilter] = useState('');

    // Comments
    const [commentsByLine, setCommentsByLine] = useState<Map<string, SCComment[]>>(new Map());
    const [openPopup, setOpenPopup] = useState<string | null>(null);
    const [newCommentText, setNewCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const popupRef = useRef<HTMLDivElement>(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await salesControllingApi.getPriceOverview({
                company: filters.company,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                customerID: customerFilter,
                productID: productFilter,
                salesManagerID: salesManagerFilter,
                supplierID: supplierFilter,
                lowMarginOnly: marginEnabled,
                marginThreshold: marginEnabled ? marginThreshold : undefined,
                deviationThreshold: deviationEnabled ? deviationThreshold : undefined,
                page,
                limit: 50,
                sortBy,
                sortOrder,
            });
            setRows(result.data.rows);
            setSummary(result.data.summary);
            if (result.data.availableFilters) {
                setAvailableSalesManagers(result.data.availableFilters.salesManagers);
                setAvailableSuppliers(result.data.availableFilters.suppliers);
                setAvailableCustomers(result.data.availableFilters.customers);
                setAvailableProducts(result.data.availableFilters.products);
            }
            setTotal(result.pagination.total);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    }, [filters.company, filters.dateFrom, filters.dateTo, customerFilter, productFilter, salesManagerFilter, supplierFilter, marginEnabled, marginThreshold, deviationEnabled, deviationThreshold, page, sortBy, sortOrder, toast]);

    const loadComments = useCallback(async () => {
        if (!filters.company) return;
        try {
            const result = await salesControllingApi.getComments(filters.company);
            const map = new Map<string, SCComment[]>();
            for (const c of result) {
                const key = `${c.documentCode}-${c.documentLineNr}`;
                const list = map.get(key) || [];
                list.push(c);
                map.set(key, list);
            }
            for (const list of map.values()) {
                list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            }
            setCommentsByLine(map);
        } catch { /* non-critical */ }
    }, [filters.company]);

    useEffect(() => { loadData(); }, [loadData, refreshKey]);
    useEffect(() => { loadComments(); }, [loadComments, refreshKey]);
    useEffect(() => { setPage(1); }, [filters.company, filters.dateFrom, filters.dateTo, customerFilter, productFilter, salesManagerFilter, supplierFilter, marginEnabled, marginThreshold, deviationEnabled, deviationThreshold, sortBy, sortOrder]);

    // Close popup on outside click
    useEffect(() => {
        if (!openPopup) return;
        const handler = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                setOpenPopup(null);
                setEditingCommentId(null);
                setNewCommentText('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openPopup]);

    const handleSort = (column: string) => {
        if (sortBy === column) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setSortBy(column); setSortOrder('desc'); }
    };

    const rowKey = (r: PriceOverviewRow) => `${r.documentCode}-${r.documentLineNr}`;

    // Comment handlers
    const handleTogglePopup = (r: PriceOverviewRow) => {
        const key = rowKey(r);
        setOpenPopup(openPopup === key ? null : key);
        setEditingCommentId(null);
        setNewCommentText('');
    };

    const handleAddComment = async (r: PriceOverviewRow) => {
        const text = newCommentText.trim();
        if (!text) return;
        try {
            await salesControllingApi.saveComment({ companyCode: r.companyCode, documentCode: r.documentCode, documentLineNr: r.documentLineNr, commentText: text });
            setNewCommentText('');
            await loadComments();
            toast('Comment added', 'success');
        } catch (err) { toast(err instanceof Error ? err.message : 'Failed to save', 'error'); }
    };

    const handleUpdateComment = async (commentId: number) => {
        const text = editText.trim();
        if (!text) return;
        try {
            await salesControllingApi.saveComment({ id: commentId, companyCode: '', documentCode: '', documentLineNr: 0, commentText: text });
            setEditingCommentId(null);
            await loadComments();
            toast('Comment updated', 'success');
        } catch (err) { toast(err instanceof Error ? err.message : 'Failed to update', 'error'); }
    };

    const handleDeleteComment = async (commentId: number) => {
        try {
            await salesControllingApi.deleteComment(commentId);
            await loadComments();
            toast('Comment removed', 'success');
        } catch (err) { toast(err instanceof Error ? err.message : 'Failed to delete', 'error'); }
    };

    const totalPages = Math.ceil(total / 50);

    const SortHeader = ({ column, label, align }: { column: string; label: string; align?: string }) => (
        <th className={`py-2 px-2 cursor-pointer hover:bg-gray-100 whitespace-nowrap ${align === 'right' ? 'text-right' : ''}`} onClick={() => handleSort(column)}>
            <span className="inline-flex items-center gap-1">
                {label}
                {sortBy === column && <ArrowUpDown className="w-3 h-3" />}
            </span>
        </th>
    );

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
                <SearchableSelect options={availableCustomers} value={customerFilter} onChange={setCustomerFilter} placeholder="All Customers" className="min-w-[180px]" />
                <SearchableSelect options={availableProducts} value={productFilter} onChange={setProductFilter} placeholder="All Products" className="min-w-[180px]" />
                <SearchableSelect options={availableSuppliers} value={supplierFilter} onChange={setSupplierFilter} placeholder="All Suppliers" className="min-w-[180px]" />
                <SearchableSelect options={availableSalesManagers} value={salesManagerFilter} onChange={setSalesManagerFilter} placeholder="All Sales Managers" className="min-w-[180px]" />
                <div className="flex items-center gap-2 text-sm whitespace-nowrap py-1.5">
                    <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={marginEnabled} onChange={e => setMarginEnabled(e.target.checked)} className="rounded" />
                        Margin &lt;
                    </label>
                    <input type="number" min={0} max={100} value={marginThreshold}
                        onChange={e => { setMarginThreshold(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0))); if (!marginEnabled) setMarginEnabled(true); }}
                        className="w-14 border rounded px-2 py-0.5 text-sm text-right" />
                    <span className="text-gray-500">%</span>
                </div>
                <div className="flex items-center gap-2 text-sm whitespace-nowrap py-1.5">
                    <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={deviationEnabled} onChange={e => setDeviationEnabled(e.target.checked)} className="rounded" />
                        Deviation &gt;
                    </label>
                    <input type="number" min={0} max={100} value={deviationThreshold}
                        onChange={e => { setDeviationThreshold(Math.min(100, Math.max(0, parseInt(e.target.value, 10) || 0))); if (!deviationEnabled) setDeviationEnabled(true); }}
                        className="w-14 border rounded px-2 py-0.5 text-sm text-right" />
                    <span className="text-gray-500">pp</span>
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap py-1.5">{total} row(s)</span>
            </div>

            {/* Data Table */}
            <div className="overflow-auto max-h-[calc(100vh-280px)] border rounded-lg">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10 shadow-sm">
                        <tr className="text-left text-xs text-gray-500 border-b">
                            <SortHeader column="PostingDate" label="Date" />
                            <SortHeader column="DocumentCode" label="Document" />
                            <SortHeader column="CustomerName" label="Customer" />
                            <SortHeader column="Incoterm" label="Incoterm" />
                            <SortHeader column="ProductName" label="Product" />
                            <SortHeader column="Quantity" label="Qty" align="right" />
                            <SortHeader column="NetSalesEUR" label="Net Sales" align="right" />
                            <SortHeader column="GrossProfitEUR" label="Gross Profit" align="right" />
                            <SortHeader column="UnitPrice" label="Unit Price" align="right" />
                            <SortHeader column="AllocatedSurcharge" label="Surcharge /u" align="right" />
                            <SortHeader column="EffectiveUnitPrice" label="Calc Price" align="right" />
                            <SortHeader column="UnitCost" label="Unit Cost" align="right" />
                            <SortHeader column="MarginPct" label="Margin %" align="right" />
                            <SortHeader column="AvgMargin12M" label="Avg 12M" align="right" />
                            <SortHeader column="MarginDeviation" label="Dev." align="right" />
                            <th className="py-2 px-2 w-16"></th>
                        </tr>
                        {/* Sticky totals row */}
                        <tr className="text-xs font-semibold border-b bg-gray-50">
                            <td className="py-1.5 px-2" colSpan={5}>Total / Avg</td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.totalQty, 0)}</td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.totalNetSales, 0)}</td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.totalGrossProfit, 0)}</td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.avgUnitPrice)}</td>
                            <td className="py-1.5 px-2 text-right"></td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.avgCalcPrice)}</td>
                            <td className="py-1.5 px-2 text-right">{fmt(summary.avgUnitCost)}</td>
                            <td className={`py-1.5 px-2 text-right font-semibold ${summary.avgMarginPct < 5 ? 'text-amber-600' : 'text-green-600'}`}>
                                {fmt(summary.avgMarginPct, 1)}%
                            </td>
                            <td className="py-1.5 px-2 text-right text-gray-500">
                                {summary.avgMargin12M != null ? `${fmt(summary.avgMargin12M, 1)}%` : ''}
                            </td>
                            <td className="py-1.5 px-2"></td>
                            <td></td>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={16} className="py-12 text-center">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                                        <span className="text-sm text-gray-400">Loading data...</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={16} className="py-8 text-center text-gray-400">No data found</td></tr>
                        )}
                        {!loading && rows.map((r, i) => {
                            const key = rowKey(r);
                            const lineComments = commentsByLine.get(key) || [];
                            const isOpen = openPopup === key;
                            const commentCount = lineComments.length;

                            return (
                                <tr key={`row-${key}-${i}`} className={`border-b ${r.isPriceBelowCost ? 'bg-red-50' : r.isLowMargin ? 'bg-amber-50' : 'hover:bg-gray-50'}`}>
                                    <td className="py-1.5 px-2 whitespace-nowrap">{new Date(r.postingDate).toLocaleDateString('de-CH')}</td>
                                    <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">
                                        {(() => {
                                            const eosUrl = getEosInvoiceUrl(r.companyCode, r.documentCode);
                                            return eosUrl ? (
                                                <a href={eosUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                                    {r.documentCode}<ExternalLink className="w-3 h-3" />
                                                </a>
                                            ) : r.documentCode;
                                        })()}
                                    </td>
                                    <td className="py-1.5 px-2 max-w-[180px] truncate">{r.customerName}</td>
                                    <td className="py-1.5 px-2 text-xs text-gray-500">{r.incoterm}</td>
                                    <td className="py-1.5 px-2 max-w-[180px] truncate">{r.productName}</td>
                                    <td className="py-1.5 px-2 text-right">{fmt(Math.abs(r.quantity), 0)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmt(r.netSalesEUR, 0)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmt(r.grossProfitEUR, 0)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmt(r.unitPrice)}</td>
                                    <td className="py-1.5 px-2 text-right">
                                        {r.allocatedSurcharge > 0 && (
                                            onNavigateToSurcharge ? (
                                                <button
                                                    onClick={() => onNavigateToSurcharge(r.documentCode)}
                                                    className="text-blue-600 hover:underline hover:text-blue-800"
                                                    title="View surcharge allocation"
                                                >
                                                    {fmt(r.allocatedSurcharge / Math.abs(r.quantity))}
                                                </button>
                                            ) : (
                                                <span className="text-blue-600">{fmt(r.allocatedSurcharge / Math.abs(r.quantity))}</span>
                                            )
                                        )}
                                    </td>
                                    <td className="py-1.5 px-2 text-right">{fmt(r.effectiveUnitPrice)}</td>
                                    <td className="py-1.5 px-2 text-right">{fmt(r.unitCost)}</td>
                                    <td className={`py-1.5 px-2 text-right font-medium ${r.isPriceBelowCost ? 'text-red-600' : r.isLowMargin ? 'text-amber-600' : 'text-green-600'}`}>
                                        {fmt(r.marginPct, 1)}%
                                    </td>
                                    <td className="py-1.5 px-2 text-right text-gray-500 text-xs">
                                        {r.avgMargin12M != null ? `${fmt(r.avgMargin12M, 1)}%` : ''}
                                    </td>
                                    <td className={`py-1.5 px-2 text-right text-xs font-medium ${r.marginDeviation != null && Math.abs(r.marginDeviation) > deviationThreshold ? (r.marginDeviation < 0 ? 'text-red-600' : 'text-green-600') : 'text-gray-400'}`}>
                                        {r.marginDeviation != null ? `${r.marginDeviation > 0 ? '+' : ''}${fmt(r.marginDeviation, 1)}` : ''}
                                    </td>
                                    <td className="py-1.5 px-2">
                                        <div className="flex items-center justify-end gap-1">
                                            {r.isPriceBelowCost && <AlertTriangle className="w-4 h-4 text-red-500" />}
                                            {r.hasUnallocatedSurcharge && onNavigateToSurcharge && (
                                                <button onClick={() => onNavigateToSurcharge(r.documentCode)} className="p-0.5 rounded hover:bg-amber-100 text-amber-500" title="Unallocated surcharges">
                                                    <ArrowRight className="w-4 h-4" />
                                                </button>
                                            )}
                                            <div className="relative">
                                                <button onClick={() => handleTogglePopup(r)} className={`p-0.5 rounded hover:bg-gray-200 relative ${commentCount > 0 ? 'text-blue-600' : 'text-gray-300 hover:text-gray-500'}`} title={commentCount > 0 ? `${commentCount} comment(s)` : 'Add comment'}>
                                                    <MessageSquare className="w-4 h-4" />
                                                    {commentCount > 0 && (
                                                        <span className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">{commentCount}</span>
                                                    )}
                                                </button>
                                                {isOpen && (
                                                    <div ref={popupRef} className="absolute right-0 top-7 z-20 bg-white border rounded-lg shadow-lg w-96">
                                                        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
                                                            <span className="text-xs font-semibold text-gray-500">Comments ({commentCount})</span>
                                                            <button onClick={() => setOpenPopup(null)} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                                                        </div>
                                                        <div className="max-h-60 overflow-y-auto">
                                                            {lineComments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No comments yet</p>}
                                                            {lineComments.map(c => (
                                                                <div key={c.id} className="px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 group">
                                                                    {editingCommentId === c.id ? (
                                                                        <div className="flex items-center gap-1">
                                                                            <input type="text" value={editText} onChange={e => setEditText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleUpdateComment(c.id); if (e.key === 'Escape') setEditingCommentId(null); }} className="flex-1 border rounded px-2 py-0.5 text-xs" autoFocus maxLength={2000} />
                                                                            <button onClick={() => handleUpdateComment(c.id)} className="p-0.5 text-green-600 hover:bg-green-100 rounded"><Check className="w-3.5 h-3.5" /></button>
                                                                            <button onClick={() => setEditingCommentId(null)} className="p-0.5 text-gray-400 hover:bg-gray-200 rounded"><X className="w-3.5 h-3.5" /></button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <p className="text-sm text-gray-800">{c.commentText}</p>
                                                                            <div className="flex items-center justify-between mt-1">
                                                                                <span className="text-[10px] text-gray-400">{formatEmail(c.createdBy)} &middot; {formatCommentDate(c.updatedAt || c.createdAt)}{c.updatedAt && ' (edited)'}</span>
                                                                                {canEdit && (
                                                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                                                                        <button onClick={() => { setEditingCommentId(c.id); setEditText(c.commentText); }} className="text-[10px] text-blue-500 hover:underline">Edit</button>
                                                                                        <button onClick={() => handleDeleteComment(c.id)} className="p-0.5 text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {canEdit && (
                                                            <div className="px-3 py-2 border-t bg-gray-50 rounded-b-lg">
                                                                <div className="flex items-center gap-1">
                                                                    <input type="text" value={newCommentText} onChange={e => setNewCommentText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAddComment(r); }} placeholder="Add a comment..." className="flex-1 border rounded px-2 py-1 text-xs" maxLength={2000} />
                                                                    <button onClick={() => handleAddComment(r)} disabled={!newCommentText.trim()} className="p-1 text-blue-600 hover:bg-blue-100 rounded disabled:opacity-30" title="Add comment"><Send className="w-3.5 h-3.5" /></button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Page {page} of {totalPages}</span>
                    <div className="flex gap-2">
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">Previous</button>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
